import React, { createContext, useContext, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { omit } from 'utils/obj'

export interface StatusChecksSearchContextType {
  authentications: {
    pageNum: number
    searchValue?: string
    setPageNum: (pageNum: number) => void
    setSearchValue: (searchValue: string) => void
  }
  statusChecks: {
    pageNum: number
    searchValue?: string
    setPageNum: (pageNum: number) => void
    setSearchValue: (searchValue: string) => void
  }
}

export const StatusChecksSearchContext =
  createContext<StatusChecksSearchContextType>({
    authentications: {
      pageNum: 0,
      searchValue: '',
      setPageNum: () => {},
      setSearchValue: () => {}
    },
    statusChecks: {
      pageNum: 0,
      searchValue: '',
      setPageNum: () => {},
      setSearchValue: () => {}
    }
  })

export function useStatusChecksSearchContext() {
  return useContext(StatusChecksSearchContext)
}

export const StatusChecksSearchProvider: React.FC = ({ children }) => {
  const [authPageNum, setAuthPageNum] = useState(0)
  const [authSearchValue, setAuthSearchValue] = useState('')
  const [checksPageNum, setChecksPageNum] = useState(0)
  const [checksSearchValue, setChecksSearchValue] = useState('')

  // support having links to pages using this hook to pass in the `clearSearchState` value in location state
  // which will then cause this to clear our settings
  // this is used primarily by the main nav buttons so they consistently land on the default view of the page
  // using this hook
  const navigate = useNavigate()

  const { pathname, state: locationState } = useLocation()
  const { clearSearchState = false } = locationState ?? {}

  useEffect(() => {
    if (clearSearchState) {
      // clear the search state
      setAuthPageNum(0)
      setAuthSearchValue('')
      setChecksPageNum(0)
      setChecksSearchValue('')
      // then remove the flag from the location state
      navigate(pathname, {
        state: omit(locationState, 'clearSearchState')
      })
    }
  }, [
    clearSearchState,
    setChecksSearchValue,
    setAuthSearchValue,
    setChecksPageNum,
    setAuthPageNum,
    navigate,
    pathname,
    locationState
  ])

  return (
    <StatusChecksSearchContext.Provider
      value={{
        authentications: {
          pageNum: authPageNum,
          searchValue: authSearchValue,
          setPageNum: setAuthPageNum,
          setSearchValue: setAuthSearchValue
        },
        statusChecks: {
          pageNum: checksPageNum,
          searchValue: checksSearchValue,
          setPageNum: setChecksPageNum,
          setSearchValue: setChecksSearchValue
        }
      }}
    >
      {children}
    </StatusChecksSearchContext.Provider>
  )
}
